import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useSearchParams } from "react-router-dom"
import { Col, Flex, Grid, Modal, Row, Tag, Typography } from "antd"
import { useTranslation } from "react-i18next"
import DataTable from "ui/DataTable"
import Widget from "components/accounting/Widget"
import TipsDetails from "components/accounting/TipsDetails"
import FormAntdModal from "components/accounting/FormAntdModal"
import DetailsToolbar from "components/accounting/DetailsToolbar"
import { useDetailsColumns } from "components/accounting/useDetailsColumns"
import DetailsCardList from "components/accounting/DetailsCardList"
import CustomPagination from "components/common/CustomPagination"

import moment from "moment"

import { useDispatch, useSelector } from "react-redux"
import { collectionAnySelector, collectionSelector } from "store/selectors"
import { cleanExpense, deleteExpense, getExpense, getAccountingTripList } from "store/accounting"

import {
  STATS_NAMES,
  FILTER_PARAM_KEYS,
  PAYMENT_TYPE_OPTIONS,
  BOOKING_TYPE_OPTIONS,
  TRANSACTION_TYPE_OPTIONS
} from "constants/accounting"
import { DATE_FORMAT } from "modules/datepicker/constants"

const EMPTY_FILTERS = Object.fromEntries(FILTER_PARAM_KEYS.map((k) => [k, []]))

const filtersFromParams = (p) =>
  Object.fromEntries(FILTER_PARAM_KEYS.map((k) => [k, p[k] ? String(p[k]).split(",").filter(Boolean) : []]))

export default function Details({ params = {}, onChangeFilters }) {
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const items = useSelector(collectionSelector("accounting"))
  const hasItems = useSelector(collectionAnySelector("accounting"))
  const currentExpense = useSelector((state) => state.accounting.expense)
  const tripList = useSelector((state) => state.accounting.tripList)
  const [filterVisible, setFilterVisible] = useState(false)
  const [draftFilters, setDraftFilters] = useState(() => filtersFromParams(params))
  const [editExpenseId, setEditExpenseId] = useState(null)
  const [deleteTargetId, setDeleteTargetId] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [toolbarHeight, setToolbarHeight] = useState(0)
  const toolbarRef = useRef(null)

  useEffect(() => { setPage(1) }, [searchTerm, params])

  useEffect(() => {
    const el = toolbarRef.current
    if (!el) return
    const observer = new ResizeObserver(() => setToolbarHeight(el.offsetHeight))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const expenseCategories = useSelector((state) => state.accounting.metadata?.expense_categories || [])

  const type = params.type || searchParams.get("type")
  const currentDate = params.date || moment().format(DATE_FORMAT)
  const currentRange = params.range || "month"
  const statsName = STATS_NAMES[type]
  const isSales = type === "sales"

  const activeFilterCount = FILTER_PARAM_KEYS.filter((key) => Boolean(params[key])).length

  const experienceOptions = useMemo(
    () => tripList.map((trip) => ({ value: String(trip.id), label: trip.name || trip.title || String(trip.id) })),
    [tripList]
  )

  const categoryOptions = useMemo(
    () => expenseCategories.map((c) => ({ value: String(c.id), label: c.name })),
    [expenseCategories]
  )

  useEffect(() => {
    dispatch(getAccountingTripList())
  }, [dispatch])

  const handleFilterOpenChange = useCallback(
    (open) => {
      if (open) setDraftFilters(filtersFromParams(params))
      setFilterVisible(open)
    },
    [params]
  )

  const handleApplyFilters = useCallback(() => {
    FILTER_PARAM_KEYS.forEach((name) => {
      const arr = draftFilters[name] || []
      onChangeFilters({ target: { name, value: arr.length ? arr.join(",") : null } })
    })
    setFilterVisible(false)
  }, [draftFilters, onChangeFilters])

  const handleCancelFilters = useCallback(() => {
    setDraftFilters(filtersFromParams(params))
    setFilterVisible(false)
  }, [params])

  const handleResetFilters = useCallback(() => {
    setDraftFilters(EMPTY_FILTERS)
    FILTER_PARAM_KEYS.forEach((name) => {
      onChangeFilters({ target: { name, value: null } })
    })
    setFilterVisible(false)
  }, [onChangeFilters])

  const appliedFilterChips = useMemo(() => {
    const filterOptions = {
      payment_type: PAYMENT_TYPE_OPTIONS,
      booking_type: BOOKING_TYPE_OPTIONS,
      transaction_type: TRANSACTION_TYPE_OPTIONS,
      experience: experienceOptions,
      category_id: categoryOptions
    }
    const chips = []
    FILTER_PARAM_KEYS.forEach((key) => {
      const paramValue = params[key]
      if (!paramValue) return
      const values = String(paramValue).split(",").filter(Boolean)
      const options = filterOptions[key] || []
      values.forEach((val) => {
        chips.push(buildChip(key, val, options))
      })
    })
    return chips

    function buildChip(key, val, options) {
      const option = options.find((o) => o.value === val)
      return { chipKey: `${key}:${val}`, filterKey: key, value: val, label: option?.label || val }
    }
  }, [params, experienceOptions, categoryOptions])

  const handleRemoveChip = useCallback(
    (filterKey, value) => {
      const paramValue = params[filterKey] || ""
      const remaining = String(paramValue)
        .split(",")
        .filter((v) => v && v !== value)
      onChangeFilters({ target: { name: filterKey, value: remaining.length ? remaining.join(",") : null } })
    },
    [params, onChangeFilters]
  )

  const applyDateRange = useCallback(
    (date, range) => {
      const m = moment(date, DATE_FORMAT)
      let gteq, lteq
      if (range === "day") {
        gteq = lteq = m.format(DATE_FORMAT)
      } else if (range === "week") {
        gteq = m.clone().startOf("isoWeek").format(DATE_FORMAT)
        lteq = m.clone().endOf("isoWeek").format(DATE_FORMAT)
      } else {
        gteq = m.clone().startOf("month").format(DATE_FORMAT)
        lteq = m.clone().endOf("month").format(DATE_FORMAT)
      }
      onChangeFilters?.({ target: { name: "date_gteq", value: gteq } })
      onChangeFilters?.({ target: { name: "date_lteq", value: lteq } })
    },
    [onChangeFilters]
  )

  const handleRangeChange = useCallback(
    (value) => {
      onChangeFilters?.({ target: { name: "range", value } })
      if (value !== "custom") {
        applyDateRange(currentDate, value)
      }
    },
    [currentDate, onChangeFilters, applyDateRange]
  )

  useEffect(() => {
    applyDateRange(currentDate, currentRange)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const searchedItems = useMemo(() => {
    if (!searchTerm.trim()) return items
    const term = searchTerm.toLowerCase()
    return items.filter((item) => {
      if (isSales) {
        return (
          String(item.transaction_id || "")
            .toLowerCase()
            .includes(term) ||
          String(item.booking_id || "")
            .toLowerCase()
            .includes(term) ||
          String(item.displayed_name || "")
            .toLowerCase()
            .includes(term) ||
          String(item.trip_title || "")
            .toLowerCase()
            .includes(term) ||
          String(item.payment_method || "")
            .toLowerCase()
            .includes(term) ||
          String(item.booking_source || "")
            .toLowerCase()
            .includes(term)
        )
      }
      return (
        String(item.displayed_name || "")
          .toLowerCase()
          .includes(term) ||
        (item.line_items || []).some(
          (li) =>
            String(li.name || "")
              .toLowerCase()
              .includes(term) ||
            String(li.category?.name || "")
              .toLowerCase()
              .includes(term)
        )
      )
    })
  }, [items, searchTerm, isSales])

  const handleEdit = useCallback(
    (record) => {
      dispatch(getExpense(record.id))
      setEditExpenseId(record.id)
    },
    [dispatch]
  )

  const handleDelete = useCallback((record) => setDeleteTargetId(record.id), [])

  const handleCloseEditModal = useCallback(() => {
    setEditExpenseId(null)
    dispatch(cleanExpense())
  }, [dispatch])

  const confirmDelete = () => {
    dispatch(deleteExpense(deleteTargetId))
    setDeleteTargetId(null)
  }


  const { columns } = useDetailsColumns({ isSales, onEdit: handleEdit, onDelete: handleDelete })

  const screens = Grid.useBreakpoint()
  const isDesktop = screens.lg

  const total = searchedItems.length
  const totalPages = Math.ceil(total / pageSize) || 1
  const paginationMeta = {
    page,
    total_pages: totalPages,
    next_page: page < totalPages ? page + 1 : null,
    prev_page: page > 1 ? page - 1 : null
  }
  const pagedItems = useMemo(
    () => searchedItems.slice((page - 1) * pageSize, page * pageSize),
    [searchedItems, page, pageSize]
  )

  const pagedTableData = useMemo(() => {
    if (isSales) return pagedItems
    return pagedItems.flatMap((expense) => {
      const lineItems = expense.line_items || []
      if (lineItems.length === 0) return [{ ...expense, _lineItem: null, _rowSpan: 1, _key: String(expense.id) }]
      return lineItems.map((lineItem, idx) => ({
        ...expense,
        _lineItem: lineItem,
        _rowSpan: idx === 0 ? lineItems.length : 0,
        _isLastLineItem: idx === lineItems.length - 1,
        _key: `${expense.id}-${idx}`
      }))
    })
  }, [isSales, pagedItems])

  const emptyText = useMemo(() => {
    const isFiltered = activeFilterCount > 0 || searchTerm.trim().length > 0
    if (hasItems && isFiltered) return t("accounting.details.empty.no_results")
    return t("accounting.details.empty.no_data")
  }, [hasItems, activeFilterCount, searchTerm, t])

  if (type === "tip_report") {
    const tipData = items && !Array.isArray(items) ? items : {}
    return <TipsDetails items={tipData.tips || []} crewMembers={tipData.crew_members || []} />
  }

  return (
    <Row gutter={[16, 16]} className="mt-m">
      <Col span={24} xxl={12}>
        <Widget stats_name={statsName} isCurrency />
      </Col>
      <Col span={24} xxl={12}>
        <Widget stats_name="booking" />
      </Col>
      <Col span={24}>
        <div
          ref={toolbarRef}
          style={{ position: "sticky", top: 0, zIndex: 10, background: "var(--color-bg-layout, #fff)", paddingBottom: 8 }}
        >
          <DetailsToolbar
            currentDate={currentDate}
            currentRange={currentRange}
            isSales={isSales}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            filterVisible={filterVisible}
            activeFilterCount={activeFilterCount}
            draftFilters={draftFilters}
            setDraftFilters={setDraftFilters}
            experienceOptions={experienceOptions}
            categoryOptions={categoryOptions}
            onFilterOpenChange={handleFilterOpenChange}
            onApplyFilters={handleApplyFilters}
            onCancelFilters={handleCancelFilters}
            onResetFilters={handleResetFilters}
            onChangeFilters={onChangeFilters}
            onRangeChange={handleRangeChange}
            dateGteq={params.date_gteq}
            dateLteq={params.date_lteq}
            onCustomDateChange={(dates) => {
              onChangeFilters?.({ target: { name: "date_gteq", value: dates.gteq } })
              onChangeFilters?.({ target: { name: "date_lteq", value: dates.lteq } })
            }}
          />

          {appliedFilterChips.length > 0 && (
            <Flex gap={8} wrap justify="flex-end" className="mt-s">
              {appliedFilterChips.map(({ chipKey, filterKey, value, label }) => (
                <Tag className="m-0" key={chipKey} closable onClose={() => handleRemoveChip(filterKey, value)} color="blue">
                  {label}
                </Tag>
              ))}
            </Flex>
          )}
        </div>

        {isDesktop ? (
          <DataTable
            dataSource={pagedTableData}
            rowKey={isSales ? "id" : "_key"}
            pagination={false}
            columns={columns}
            locale={{ emptyText }}
            scroll={{ x: "max-content" }}
            sticky={{ offsetHeader: toolbarHeight }}
          />
        ) : (
          <DetailsCardList
            items={pagedItems}
            isSales={isSales}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
        <CustomPagination
          metadata={paginationMeta}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(1) }}
        />
      </Col>

      <Modal
        open={!!deleteTargetId}
        title={t("expense.delete_title")}
        okText={t("expense.delete")}
        okButtonProps={{ danger: true }}
        onOk={confirmDelete}
        onCancel={() => setDeleteTargetId(null)}
      >
        <Typography.Text>{t("accounting.details.delete_description")}</Typography.Text>
      </Modal>

      {editExpenseId && (
        <FormAntdModal
          visible={!!editExpenseId}
          setVisible={handleCloseEditModal}
          expenseId={editExpenseId}
          expense={currentExpense}
          onClose={handleCloseEditModal}
        />
      )}
    </Row>
  )
}

Details.propTypes = {
  params: PropTypes.shape({
    type: PropTypes.string,
    date: PropTypes.string,
    range: PropTypes.string,
    payment_type: PropTypes.string,
    booking_type: PropTypes.string,
    transaction_type: PropTypes.string,
    experience: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    category_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  onChangeFilters: PropTypes.func
}
